// src/components/MainPage/MainPage.js
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SupportPage from '../SupportPage/SupportPage';
import ContactsPage from '../ContactsPage/ContactsPage';
import EducationalBlock from '../EducationalBlock/EducationalBlock';
import { createServicesDatabase, getActionByName } from '../../data/servicesData';

// Добавляем типы для WebApp
const WebApp = window.Telegram?.WebApp;

const MainPage = (props) => {
  const { t, i18n } = useTranslation();
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const [showSupport, setShowSupport] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(true);
  
  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  const languages = [
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'tt', name: 'Татарча', flag: '🇹🇭' },
    { code: 'fr', name: 'Français', flag: '🇫🇷'},
    { code: 'pt', name: 'Português', flag: '🇵🇹'},
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' }
  ];

  const getCurrentLanguage = () => {
    return languages.find(lang => lang.code === i18n.language) || languages[0];
  };

  // Инициализация голосового распознавания
  useEffect(() => {
    // Проверяем поддержку Web Speech API
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setVoiceSupported(false);
      console.log('Голосовой поиск не поддерживается в этом браузере');
      return;
    }

    // Создаем экземпляр распознавания речи
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    
    // Настройки распознавания
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = false;
    recognitionRef.current.lang = 'ru-RU';
    
    // Обработчик результатов
    recognitionRef.current.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      console.log('Распознано:', transcript);
      
      setSearchQuery(transcript);
      performSearch(transcript);
      
      if (WebApp) {
        WebApp.HapticFeedback.notificationOccurred('success');
      }
      
      setIsListening(false);
    };

    // Обработчик ошибок
    recognitionRef.current.onerror = (event) => {
      console.error('Ошибка распознавания речи:', event.error);
      
      if (WebApp) {
        WebApp.HapticFeedback.notificationOccurred('error');
      }
      
      setIsListening(false);
      
      if (event.error === 'not-allowed') {
        alert('Пожалуйста, разрешите доступ к микрофону для использования голосового поиска');
      } else if (event.error === 'no-speech') {
        alert('Речь не обнаружена. Попробуйте еще раз');
      } else {
        alert('Произошла ошибка при распознавании речи');
      }
    };

    // Обработчик окончания распознавания
    recognitionRef.current.onend = () => {
      setIsListening(false);
    };

    // Очистка при размонтировании
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Обновляем язык распознавания при смене языка интерфейса
  useEffect(() => {
    if (recognitionRef.current) {
      const langMap = {
        'ru': 'ru-RU',
        'en': 'en-US',
        'tt': 'ru-RU', // Для татарского используем русский движок
        'fr': 'fr-FR',
        'pt': 'pt-PT',
        'hi': 'hi-IN'
      };
      
      const recognitionLang = langMap[i18n.language] || 'ru-RU';
      recognitionRef.current.lang = recognitionLang;
    }
  }, [i18n.language]);

  // Функция для нормализации строки
  const normalizeString = (str) => {
    if (!str) return '';
    return str.toLowerCase()
      .replace(/[^\w\sа-яА-ЯёЁa-zA-Z]/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  // Функция для транслитерации (конвертация раскладки)
  const transliterate = (text) => {
    if (!text) return text;
    
    // Словарь для конвертации английской раскладки в русскую
    const enToRuMap = {
      'q': 'й', 'w': 'ц', 'e': 'у', 'r': 'к', 't': 'е', 'y': 'н', 'u': 'г',
      'i': 'ш', 'o': 'щ', 'p': 'з', '[': 'х', ']': 'ъ', 'a': 'ф', 's': 'ы',
      'd': 'в', 'f': 'а', 'g': 'п', 'h': 'р', 'j': 'о', 'k': 'л', 'l': 'д',
      ';': 'ж', "'": 'э', 'z': 'я', 'x': 'ч', 'c': 'с', 'v': 'м', 'b': 'и',
      'n': 'т', 'm': 'ь', ',': 'б', '.': 'ю', '/': '.',
      // Заглавные буквы
      'Q': 'Й', 'W': 'Ц', 'E': 'У', 'R': 'К', 'T': 'Е', 'Y': 'Н', 'U': 'Г',
      'I': 'Ш', 'O': 'Щ', 'P': 'З', '{': 'Х', '}': 'Ъ', 'A': 'Ф', 'S': 'Ы',
      'D': 'В', 'F': 'А', 'G': 'П', 'H': 'Р', 'J': 'О', 'K': 'Л', 'L': 'Д',
      ':': 'Ж', '"': 'Э', 'Z': 'Я', 'X': 'Ч', 'C': 'С', 'V': 'М', 'B': 'И',
      'N': 'Т', 'M': 'Ь', '<': 'Б', '>': 'Ю', '?': ','
    };
    
    // Словарь для конвертации русской раскладки в английскую
    const ruToEnMap = {
      'й': 'q', 'ц': 'w', 'у': 'e', 'к': 'r', 'е': 't', 'н': 'y', 'г': 'u',
      'ш': 'i', 'щ': 'o', 'з': 'p', 'х': '[', 'ъ': ']', 'ф': 'a', 'ы': 's',
      'в': 'd', 'а': 'f', 'п': 'g', 'р': 'h', 'о': 'j', 'л': 'k', 'д': 'l',
      'ж': ';', 'э': "'", 'я': 'z', 'ч': 'x', 'с': 'c', 'м': 'v', 'и': 'b',
      'т': 'n', 'ь': 'm', 'б': ',', 'ю': '.', '.': '/',
      // Заглавные
      'Й': 'Q', 'Ц': 'W', 'У': 'E', 'К': 'R', 'Е': 'T', 'Н': 'Y', 'Г': 'U',
      'Ш': 'I', 'Щ': 'O', 'З': 'P', 'Х': '{', 'Ъ': '}', 'Ф': 'A', 'Ы': 'S',
      'В': 'D', 'А': 'F', 'П': 'G', 'Р': 'H', 'О': 'J', 'Л': 'K', 'Д': 'L',
      'Ж': ':', 'Э': '"', 'Я': 'Z', 'Ч': 'X', 'С': 'C', 'М': 'V', 'И': 'B',
      'Т': 'N', 'Ь': 'M', 'Б': '<', 'Ю': '>', ',': '?'
    };
    
    // Определяем, в какой раскладке написан текст
    // Считаем количество русских и английских букв
    let ruCount = 0;
    let enCount = 0;
    
    for (let char of text) {
      if (/[а-яА-ЯёЁ]/.test(char)) ruCount++;
      if (/[a-zA-Z]/.test(char)) enCount++;
    }
    
    // Если больше английских букв, конвертируем в русскую раскладку
    if (enCount > ruCount) {
      return text.split('').map(char => enToRuMap[char] || char).join('');
    }
    // Если больше русских букв, конвертируем в английскую раскладку
    else if (ruCount > enCount) {
      return text.split('').map(char => ruToEnMap[char] || char).join('');
    }
    
    // Если поровну или нет букв, возвращаем как есть
    return text;
  };

  // Функция для получения всех вариантов запроса (включая транслитерацию)
  const getQueryVariants = (query) => {
    const variants = new Set();
    const normalized = normalizeString(query);
    
    // Добавляем оригинальный запрос
    variants.add(normalized);
    
    // Добавляем транслитерированную версию
    const transliterated = normalizeString(transliterate(query));
    if (transliterated !== normalized) {
      variants.add(transliterated);
    }
    
    // Для каждого слова в запросе также пробуем транслитерацию
    const words = normalized.split(/\s+/);
    words.forEach(word => {
      const translitWord = normalizeString(transliterate(word));
      if (translitWord !== word) {
        variants.add(translitWord);
      }
    });
    
    // Пробуем комбинации: транслитерируем только первое слово, только второе и т.д.
    if (words.length > 1) {
      for (let i = 0; i < words.length; i++) {
        const mixedVariant = words.map((word, index) => 
          index === i ? normalizeString(transliterate(word)) : word
        ).join(' ');
        variants.add(mixedVariant);
      }
    }
    
    console.log('Варианты запроса:', Array.from(variants));
    return Array.from(variants);
  };

  // Функция для удаления всех пробелов (для поиска по словам без учета порядка)
  const removeSpaces = (str) => {
    if (!str) return '';
    return str.replace(/\s+/g, '');
  };

  // Словарь синонимов и английских терминов
  const synonyms = {
    // Врачи и специалисты
    'педиатр': ['педиатр', 'детский врач', 'доктор для детей', 'педиатрия', 'детский', 'children', 'pediatrician', 'pediatrics', 'child doctor', 'pedia', 'pediatr'],
    'психолог': ['психолог', 'психотерапевт', 'психология', 'душевное здоровье', 'психологическая помощь', 'psychologist', 'psychology', 'therapist', 'counselor', 'mental health', 'psiholog', 'psycho'],
    'психиатр': ['психиатр', 'психиатрия', 'психическое здоровье', 'psychiatrist', 'psychiatry', 'mental health', 'psihiatr'],
    'лор': ['лор', 'отоларинголог', 'оториноларинголог', 'ухо', 'горло', 'нос', 'ухо-горло-нос', 'ent', 'ear', 'nose', 'throat', 'otolaryngologist', 'ent doctor', 'lor'],
    'стоматолог': ['стоматолог', 'дантист', 'зубной врач', 'зубы', 'dentist', 'dental', 'teeth', 'dental doctor', 'stomatolog', 'dent'],
    'гинеколог': ['гинеколог', 'женский врач', 'женское здоровье', 'гинекология', 'gynecologist', 'women health', 'gynaecologist', 'obstetrician', 'ginekolog', 'gyn'],
    'дерматолог': ['дерматолог', 'кожный врач', 'кожа', 'дерматология', 'dermatologist', 'skin', 'dermatology', 'skin doctor', 'dermatolog', 'derma'],
    'невролог': ['невролог', 'невропатолог', 'нервы', 'головная боль', 'neurologist', 'neurology', 'nerve doctor', 'nevrolog', 'neuro'],
    'офтальмолог': ['офтальмолог', 'окулист', 'глазной врач', 'глаза', 'зрение', 'ophthalmologist', 'eye doctor', 'optometrist', 'eye specialist', 'oftalmolog', 'oculist'],
    
    // Анализы и диагностика
    'кровь': ['кровь', 'анализ крови', 'гемоглобин', 'лейкоциты', 'эритроциты', 'соэ', 'blood', 'blood test', 'hematology', 'cbc', 'complete blood count', 'krov'],
    'анализы': ['анализы', 'лаборатория', 'исследования', 'лаб', 'лабораторные исследования', 'tests', 'lab', 'laboratory', 'analysis', 'lab tests', 'analizy'],
    'моча': ['моча', 'анализ мочи', 'урина', 'оам', 'общий анализ мочи', 'urine', 'urinalysis', 'urine test', 'mocha'],
    'кал': ['кал', 'анализ кала', 'копрограмма', 'фекалии', 'stool', 'feces', 'coprology', 'stool analysis', 'kal'],
    'биохимия': ['биохимия', 'биохимический анализ', 'холестерин', 'глюкоза', 'креатинин', 'biochemistry', 'blood chemistry', 'biochemical analysis', 'biohimiya'],
    'рентген': ['рентген', 'рентгенография', 'снимок', 'флюорография', 'xray', 'x-ray', 'radiography', 'xray image', 'rentgen', 'xray'],
    'мрт': ['мрт', 'магнитно-резонансная томография', 'магнитный резонанс', 'mri', 'magnetic resonance', 'magnetic resonance imaging', 'mrt'],
    'кт': ['кт', 'компьютерная томография', 'томография', 'сканер', 'ct', 'cat scan', 'computed tomography', 'ct scan', 'kt'],
    'экг': ['экг', 'электрокардиограмма', 'сердце', 'кардиограмма', 'ecg', 'ekg', 'electrocardiogram', 'heart test', 'ekg'],
    'холтер': ['холтер', 'холтеровское мониторирование', 'суточное мониторирование', 'holter', 'holter monitor', 'holter monitoring', 'holter'],
    
    // Услуги и процедуры
    'медосмотр': ['медосмотр', 'медицинский осмотр', 'профосмотр', 'комиссия', 'медкомиссия', 'профилактический осмотр', 'checkup', 'medical examination', 'health check', 'medosmotr'],
    'чекап': ['чекап', 'check-up', 'checkup', 'комплексное обследование', 'скрининг', 'комплексная диагностика', 'comprehensive examination', 'screening', 'chekap'],
    'косметология': ['косметология', 'косметолог', 'уход за кожей', 'красота', 'косметические процедуры', 'cosmetology', 'beauty', 'skin care', 'beauty treatments', 'kosmetologiya'],
    'удаление': ['удаление', 'новообразования', 'папилломы', 'бородавки', 'родинки', 'removal', 'mole removal', 'wart removal', 'skin lesion removal', 'udalenie'],
    'фототерапия': ['фототерапия', 'светолечение', 'phototherapy', 'light therapy', 'phototherapy treatment', 'fototerapiya'],
    
    // Общие термины
    'врач': ['врач', 'доктор', 'специалист', 'прием', 'консультация', 'doctor', 'physician', 'specialist', 'medical specialist', 'vrach'],
    'контакты': ['контакты', 'адрес', 'телефон', 'локация', 'как добраться', 'где найти', 'contacts', 'address', 'phone', 'location', 'contact information', 'kontakty'],
    'отзывы': ['отзывы', 'рейтинг', 'мнение пациентов', 'reviews', 'ratings', 'testimonials', 'patient feedback', 'otzyvy'],
    'вопросы': ['вопросы', 'faq', 'часто задаваемые вопросы', 'помощь', 'справка', 'questions', 'help', 'faqs', 'frequently asked questions', 'voprosy'],
    'поддержка': ['поддержка', 'техподдержка', 'помощь', 'чат', 'консультация', 'support', 'helpdesk', 'assistance', 'customer support', 'podderzhka'],
    'цена': ['цена', 'прайс', 'стоимость', 'услуги', 'прайс-лист', 'price', 'cost', 'prices', 'pricing', 'price list', 'cena'],
    
    // Дополнительные термины
    'сердце': ['сердце', 'кардиология', 'кардиолог', 'сердечно-сосудистый', 'heart', 'cardiology', 'cardiologist', 'cardiovascular', 'serdce'],
    'зуб': ['зуб', 'зубы', 'стоматология', 'dental', 'tooth', 'teeth', 'dentistry', 'zub'],
    'кожа': ['кожа', 'кожные заболевания', 'дерматология', 'skin', 'skin diseases', 'dermatology', 'kozha'],
    'инфекции': ['инфекции', 'инфекционный контроль', 'ковид', 'грипп', 'вирусы', 'infections', 'infectious diseases', 'covid', 'flu', 'viruses', 'infektsii'],
    'женское': ['женское', 'женский', 'гинекология', 'women', 'female', 'gynecology', 'women health', 'zhenskoe'],
    'мужское': ['мужское', 'мужской', 'урология', 'андрология', 'men', 'male', 'urology', 'andrology', 'muzhskoe'],
    'сосуды': ['сосуды', 'сосудистый', 'купероз', 'розацеа', 'сосудистые звездочки', 'vessels', 'vascular', 'couperose', 'rosacea', 'spider veins', 'sosudy']
  };

  // Функция для получения всех синонимов для слова
  const getSynonyms = (word) => {
    // Ищем точное совпадение в ключах синонимов
    if (synonyms[word]) {
      return synonyms[word];
    }
    
    // Ищем частичное совпадение в ключах
    for (const [key, values] of Object.entries(synonyms)) {
      if (word.includes(key) || key.includes(word)) {
        return values;
      }
    }
    
    // Ищем в значениях синонимов
    for (const values of Object.values(synonyms)) {
      if (values.some(v => v.includes(word))) {
        return values;
      }
    }
    
    // Если синонимы не найдены, возвращаем само слово
    return [word];
  };

  // Функция для проверки совпадения слова с элементом
  const wordMatchesItem = (word, item, synonymsList) => {
    const normalizedTitle = normalizeString(item.title);
    const normalizedKeywords = item.keywords.map(k => normalizeString(k));
    const normalizedParent = item.parent ? normalizeString(item.parent) : '';
    const normalizedSpecialty = item.specialty ? normalizeString(item.specialty) : '';
    
    // Проверяем все синонимы
    return synonymsList.some(synonym => {
      const normalizedSynonym = normalizeString(synonym);
      
      return normalizedTitle.includes(normalizedSynonym) ||
             normalizedKeywords.some(k => k.includes(normalizedSynonym)) ||
             normalizedParent.includes(normalizedSynonym) ||
             normalizedSpecialty.includes(normalizedSynonym);
    });
  };

  // Функция поиска с поддержкой транслитерации
  const performSearch = (query) => {
    if (!query || query.trim().length < 1) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    console.log('Поиск по запросу:', query);

    const services = createServicesDatabase();
    
    // Получаем все варианты запроса (включая транслитерированные)
    const queryVariants = getQueryVariants(query);
    
    const allResults = new Map(); // Используем Map для уникальных результатов
    
    queryVariants.forEach(variant => {
      const normalizedQuery = normalizeString(variant);
      
      // Разбиваем запрос на отдельные слова
      const queryWords = normalizedQuery.split(/\s+/).filter(w => w.length > 0);
      
      console.log(`Поиск по варианту "${variant}" (слова: ${queryWords.join(', ')})`);
      
      // Если только одно слово - используем обычный поиск
      if (queryWords.length === 1) {
        const word = queryWords[0];
        const wordSynonyms = getSynonyms(word);
        
        services.forEach(item => {
          if (wordMatchesItem(word, item, wordSynonyms) && !allResults.has(item.id)) {
            // Рассчитываем релевантность
            let score = 0;
            const normalizedTitle = normalizeString(item.title);
            
            wordSynonyms.forEach(synonym => {
              const normalizedSynonym = normalizeString(synonym);
              if (normalizedTitle.includes(normalizedSynonym)) score += 10;
              if (item.keywords.some(k => normalizeString(k).includes(normalizedSynonym))) score += 5;
            });
            
            allResults.set(item.id, {
              item,
              score: score + 5, // Добавляем бонус за вариант поиска
              matchedWordsCount: 1
            });
          }
        });
      } else {
        // Мультисловный поиск
        const wordsWithSynonyms = queryWords.map(word => ({
          original: word,
          synonyms: getSynonyms(word)
        }));
        
        services.forEach(item => {
          if (allResults.has(item.id)) return; // Уже добавили
          
          let matched = false;
          let totalScore = 0;
          const matchedWords = new Set();
          
          wordsWithSynonyms.forEach(({original, synonyms}) => {
            let wordMatched = false;
            let wordScore = 0;
            
            synonyms.forEach(synonym => {
              const normalizedSynonym = normalizeString(synonym);
              const normalizedTitle = normalizeString(item.title);
              const normalizedKeywords = item.keywords.map(k => normalizeString(k));
              const normalizedParent = item.parent ? normalizeString(item.parent) : '';
              const normalizedSpecialty = item.specialty ? normalizeString(item.specialty) : '';
              
              if (normalizedTitle.includes(normalizedSynonym)) {
                wordMatched = true;
                wordScore = Math.max(wordScore, 10);
              }
              
              if (normalizedKeywords.some(k => k.includes(normalizedSynonym))) {
                wordMatched = true;
                wordScore = Math.max(wordScore, 5);
              }
              
              if (normalizedParent.includes(normalizedSynonym)) {
                wordMatched = true;
                wordScore = Math.max(wordScore, 3);
              }
              
              if (normalizedSpecialty.includes(normalizedSynonym)) {
                wordMatched = true;
                wordScore = Math.max(wordScore, 3);
              }
            });
            
            if (wordMatched) {
              matched = true;
              matchedWords.add(original);
              totalScore += wordScore;
            }
          });
          
          if (matched) {
            totalScore += matchedWords.size * 2;
            allResults.set(item.id, {
              item,
              score: totalScore + 3, // Бонус за вариант поиска
              matchedWordsCount: matchedWords.size
            });
          }
        });
      }
    });
    
    // Если ничего не найдено, пробуем поиск без учета порядка слов
    if (allResults.size === 0) {
      console.log('Пробуем поиск без учета порядка слов...');
      
      queryVariants.forEach(variant => {
        const normalizedQuery = normalizeString(variant);
        const combinedQuery = removeSpaces(normalizedQuery);
        const queryWords = normalizedQuery.split(/\s+/).filter(w => w.length > 0);
        
        services.forEach(item => {
          if (allResults.has(item.id)) return;
          
          const combinedItem = removeSpaces(
            normalizeString(item.title) + ' ' + 
            item.keywords.map(k => normalizeString(k)).join(' ') + ' ' + 
            (item.parent ? normalizeString(item.parent) : '') + ' ' + 
            (item.specialty ? normalizeString(item.specialty) : '')
          );
          
          if (combinedItem.includes(combinedQuery)) {
            allResults.set(item.id, {
              item,
              score: 5,
              matchedWordsCount: 1
            });
          } else {
            // Проверяем по отдельным словам
            queryWords.forEach(word => {
              const wordSynonyms = getSynonyms(word);
              wordSynonyms.forEach(synonym => {
                if (combinedItem.includes(removeSpaces(normalizeString(synonym)))) {
                  allResults.set(item.id, {
                    item,
                    score: 3,
                    matchedWordsCount: 1
                  });
                }
              });
            });
          }
        });
      });
    }
    
    // Преобразуем Map в массив и сортируем
    const sortedResults = Array.from(allResults.values())
      .sort((a, b) => {
        if (a.matchedWordsCount !== b.matchedWordsCount) {
          return b.matchedWordsCount - a.matchedWordsCount;
        }
        return b.score - a.score;
      })
      .map(r => r.item);
    
    console.log('Итоговые результаты:', sortedResults.length);
    setSearchResults(sortedResults.slice(0, 15));
    setShowResults(sortedResults.length > 0);
    setSelectedResultIndex(-1);
  };

  // Функция для запуска голосового поиска
  const startVoiceSearch = async () => {
    if (!recognitionRef.current) {
      alert('Голосовой поиск не поддерживается в вашем браузере');
      return;
    }

    try {
      // Запрашиваем разрешение на использование микрофона с оптимальными настройками
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      // Останавливаем треки, чтобы освободить микрофон
      stream.getTracks().forEach(track => track.stop());
      
      // Запускаем распознавание
      recognitionRef.current.start();
      setIsListening(true);
      
      if (WebApp) {
        WebApp.HapticFeedback.impactOccurred('medium');
      }
      
      // Автоматически останавливаем через 7 секунд
      setTimeout(() => {
        if (isListening) {
          recognitionRef.current.stop();
        }
      }, 7000);
      
    } catch (err) {
      console.error('Ошибка доступа к микрофону:', err);
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        alert('Для использования голосового поиска необходимо разрешить доступ к микрофону');
      } else if (err.name === 'NotFoundError') {
        alert('Микрофон не найден на устройстве');
      } else {
        alert('Не удалось запустить голосовой поиск');
      }
    }
  };

  // Остановка голосового поиска
  const stopVoiceSearch = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  // Обработчик изменения поискового запроса
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    performSearch(value);
  };

  // Обработчик клика по результату поиска
const handleResultClick = (result) => {
  console.log('Клик по результату:', result);
  
  // Для врачей передаем дополнительные данные
  const action = getActionByName(
    result.action, 
    {
      ...props,
      handleOpenSupport,
      handleOpenContacts
    },
    result.doctorData || null // Передаем данные врача для onOpenDoctorServices
  );
  
  if (action) {
    console.log('Выполняем действие:', result.action);
    action();
  } else {
    console.log('Действие не найдено:', result.action);
  }
  
  setSearchQuery('');
  setShowResults(false);
};

  // Обработчик нажатия клавиш
  const handleKeyDown = (e) => {
    if (!showResults) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedResultIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedResultIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedResultIndex >= 0 && searchResults[selectedResultIndex]) {
          handleResultClick(searchResults[selectedResultIndex]);
        } else if (searchResults.length > 0) {
          handleResultClick(searchResults[0]);
        }
        break;
      case 'Escape':
        setShowResults(false);
        break;
      default:
        break;
    }
  };

  // Закрытие результатов при клике вне области
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    setShowLanguageModal(false);
  };

  // Функции для открытия страниц
  const handleOpenSupport = () => {
    setShowSupport(true);
  };

  const handleOpenContacts = () => {
    setShowContacts(true);
  };

  // Если открыта страница поддержки
  if (showSupport) {
    return (
      <div className="support-page-wrapper">
        <SupportPage onBack={() => setShowSupport(false)} />
      </div>
    );
  }

  // Если открыта страница контактов
  if (showContacts) {
    return (
      <ContactsPage onBack={() => setShowContacts(false)} />
    );
  }

  return (
    <>
      {/* Шапка с логотипом */}
      <header className="header">
        <div className="clinic-logo">
          <svg 
            className="clinic-logo-svg"
            viewBox="700 350 500 300" 
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <style>
                {`
                  .logo-st0{fill:#FFFFFF;}
                  .logo-st1{fill:#FFFFFF;}
                `}
              </style>
            </defs>
            <path className="logo-st0" d="M810.8,408c-14-0.7-25.3-11.5-26.9-25.3c-1.1-10.6-9.6-18.7-20.1-19.7c0,0,0,0-0.1,0c0,0,0,0-0.1,0c-0.3,0-0.6,0-0.8,0c-0.1,0-0.3,0-0.4,0c-0.1,0-0.4,0-0.6,0c-0.1,0-0.4,0-0.6,0c-0.1,0-0.3,0-0.4,0c-0.3,0-0.6,0-0.8,0c0,0,0,0-0.1,0c0,0,0,0-0.1,0c-10.5,0.8-19,9.1-20.1,19.7c-1.6,13.9-12.9,24.6-26.9,25.3c-12.2,0.6-21.6,10.6-21.6,22.9c0,7.2,3.5,14.1,9.3,18.5c4,3,8.6,4.4,13.6,4.4c11.7,0,21.8-9.1,22.8-20.8c1-10.2,7.2-18.7,15.8-22.9c5.7-2.8,12.4-2.8,18.1,0c8.8,4.2,15,12.9,15.8,22.9c1.1,11.6,11,20.8,22.8,20.8c5,0,9.5-1.6,13.6-4.4c5.8-4.2,9.3-11.2,9.3-18.5C832.4,418.7,823,408.7,810.8,408z"/>
            <path className="logo-st0" d="M761.7,496.6c12,0,21.8-9.7,21.8-21.8c0-12-9.7-21.8-21.8-21.8c-12,0-21.8,9.7-21.8,21.8C739.9,486.9,749.7,496.6,761.7,496.6z"/>
            <path className="logo-st1" d="M852.1,528.6c0,1.8,1,2.8,3.1,2.8c0.6,0,1.1-0.1,1.7-0.3v9.8c-2.4,1-4.9,1.4-7.9,1.4c-5,0-8.5-1.7-10.6-5c-4.5,3.5-9.8,5.4-15.4,5.4c-4.4,0-8.1-1.3-11-3.7c-3-2.4-4.4-5.8-4.4-10c0-2.7,0.6-5,1.7-6.9c1.1-2,2.7-3.5,4.7-4.5c2-1,4-1.8,6.1-2.3c2.1-0.4,4.4-0.7,6.6-0.7c3.4,0,6.5,0.3,9.6,1v-1.7c0-1.7-0.8-3-2.4-4c-1.6-1-3.8-1.6-6.9-1.6c-4.7,0-9.9,1.1-15.6,3.4v-13.2c6.1-2,11.7-3,17.3-3c7.6,0,13.3,1.6,17.3,4.7c4,3.1,5.9,8.5,5.9,16v12.3H852.1z M823.7,527.7c0,1.3,0.4,2.3,1.4,3.1c1,0.8,2.3,1.3,3.8,1.3c2.8,0,5.2-0.7,7.4-2.3v-5.9c-1.7-0.6-3.5-0.8-5.7-0.8C825.9,522.9,823.7,524.5,823.7,527.7z"/>
            <path className="logo-st1" d="M884.9,508.9c-0.3,5-0.8,9.3-1.4,13.2c-0.7,3.7-1.6,7.2-2.8,10.5s-3,5.7-5.2,7.4c-2.1,1.7-4.8,2.5-7.8,2.5c-2.4,0-5.1-0.4-7.9-1.4v-12.4c1,0.4,1.7,0.6,2.5,0.6c1.1,0,2.1-0.4,2.8-1.1c0.8-0.7,1.6-1.8,2.1-3.4c0.6-1.6,1.1-3.1,1.4-4.8c0.4-1.7,0.7-3.8,1-6.6c0.3-2.8,0.4-5.2,0.6-7.5c0.1-2.1,0.3-5.1,0.4-8.6h37.9v44.4h-15.7V509h-7.9V508.9z"/>
            <path className="logo-st1" d="M959.9,528.6c0,1.8,1,2.8,3.1,2.8c0.6,0,1.1-0.1,1.7-0.3v9.8c-2.4,1-5,1.4-7.9,1.4c-4.9,0-8.5-1.7-10.6-5c-4.5,3.5-9.8,5.4-15.4,5.4c-4.4,0-8.1-1.3-11-3.7c-3-2.4-4.4-5.8-4.4-10c0-2.7,0.6-5,1.7-6.9c1.1-2,2.7-3.5,4.7-4.5c2-1,4-1.8,6.1-2.3c2.1-0.4,4.4-0.7,6.6-0.7c3.4,0,6.5,0.3,9.6,1v-1.7c0-1.7-0.8-3-2.4-4c-1.6-1-3.8-1.6-6.9-1.6c-4.7,0-9.9,1.1-15.6,3.4v-13.2c6.1-2,11.7-3,17.3-3c7.6,0,13.3,1.6,17.3,4.7c4,3.1,5.9,8.5,5.9,16v12.3H959.9z M931.4,527.7c0,1.3,0.4,2.3,1.4,3.1c1,0.8,2.3,1.3,3.8,1.3c2.8,0,5.2-0.7,7.4-2.3v-5.9c-1.7-0.6-3.5-0.8-5.7-0.8C933.8,522.9,931.4,524.5,931.4,527.7z"/>
            <path className="logo-st1" d="M1017.7,521.3c0,6.8-2.3,12.2-6.8,15.7c-4.5,3.7-10.3,5.5-17.3,5.5c-7.1,0-12.9-2.1-17.4-6.5c-4.5-4.4-6.6-11.3-6.6-20.8c0-14.3,4.5-24.3,13.6-30c3.5-2.3,7.5-3.8,11.9-4.7c4.4-1,8.2-1.7,11.3-2.3c3.3-0.6,5.7-1.7,7.4-3.3V489c-2.1,2-5.7,3.1-10.5,3.7c-4.9,0.6-8.5,1.4-10.7,2.5c-2.1,1-3.8,2.7-5,4.8c-1.3,2.1-1.8,4.2-1.8,6.2c3.4-4.1,7.8-6.2,13.4-6.2c5.5,0,9.9,1.8,13.3,5.7C1016,509.5,1017.7,514.7,1017.7,521.3z M986,518.5c-0.1,0.8-0.3,1.8-0.3,2.8s0.1,2,0.3,2.8c0.1,0.8,0.6,1.8,1,3c0.4,1,1.3,1.8,2.4,2.5c1.1,0.7,2.5,1,4.1,1c1.6,0,3-0.3,4.1-1c1.1-0.7,2-1.6,2.4-2.5c0.4-1,0.8-2,1-3c0.1-0.8,0.3-1.8,0.3-2.8s-0.1-2-0.3-2.8c-0.1-0.8-0.6-1.8-1-3c-0.4-1-1.3-1.8-2.4-2.5c-1.1-0.7-2.5-1-4.1-1c-1.6,0-3,0.3-4.1,1c-1.1,0.7-2,1.6-2.4,2.5C986.6,516.7,986.2,517.7,986,518.5z"/>
            <path className="logo-st1" d="M1045,523.3l9.1-26.4h16.1l-18.5,49.1c-4.4,11.3-11,17-20.4,17c-2.3,0-5-0.4-8.2-1.3v-13c2.1,0.4,4.1,0.7,5.9,0.7c4.1,0,7.2-2.4,9.2-7.1l-20.6-45.4h16.7L1045,523.3z"/>
            <path className="logo-st1" d="M1091.4,541.3h-15.7v-44.4h34.7v13h-18.8v31.4H1091.4z"/>
            <path className="logo-st1" d="M1157.3,528.6c0,1.8,1,2.8,3.1,2.8c0.6,0,1.1-0.1,1.7-0.3v9.8c-2.4,1-4.9,1.4-7.9,1.4c-5,0-8.5-1.7-10.6-5c-4.5,3.5-9.8,5.4-15.4,5.4c-4.4,0-8.1-1.3-11-3.7c-3-2.4-4.4-5.8-4.4-10c0-2.7,0.6-5,1.7-6.9c1.1-2,2.7-3.5,4.7-4.5c2-1,4-1.8,6.1-2.3c2.1-0.4,4.4-0.7,6.7-0.7c3.4,0,6.5,0.3,9.6,1v-1.7c0-1.7-0.8-3-2.4-4c-1.6-1-3.8-1.6-6.9-1.6c-4.7,0-9.9,1.1-15.6,3.4v-13.2c6.1-2,11.7-3,17.3-3c7.6,0,13.3,1.6,17.2,4.7c4,3.1,5.9,8.5,5.9,16v12.3H1157.3z M1128.9,527.7c0,1.3,0.4,2.3,1.4,3.1c1,0.8,2.3,1.3,3.8,1.3c2.8,0,5.2-0.7,7.4-2.3v-5.9c-1.7-0.6-3.5-0.8-5.7-0.8C1131.3,522.9,1128.9,524.5,1128.9,527.7z"/>
            <path className="logo-st0" d="M813.3,587.2h-6.5v-23.1h6.4l7.2,11.5l7.2-11.5h6.4v23.1h-6.4v-13.6l-5.4,8.7h-3.3l-5.4-8.7v13.6H813.3z"/>
            <path className="logo-st0" d="M838.5,575.6c0-2.1,0.3-3.9,1.1-5.6c0.8-1.6,1.6-2.9,2.9-3.8c1.1-1,2.5-1.6,3.8-2.1c1.3-0.5,2.6-0.7,4.1-0.7c2.3,0,4.3,0.5,6.1,1.3c1.8,0.8,2.9,2,3.8,3.3c0.8,1.3,1.3,2.9,1.6,4.6c0.3,1.6,0.2,3.4-0.3,5.1h-16.4c0.3,1.6,1.3,2.8,2.8,3.6c1.5,0.8,3.3,1.1,5.4,1.1c2.6,0,4.9-0.5,7-1.5v5.2c-2.3,1-5.1,1.5-8.5,1.5c-3.9,0-7-1.1-9.7-3.3C839.7,582.4,838.5,579.5,838.5,575.6z M855.5,573.4c0.2-1-0.2-2.1-1-3.3c-0.8-1.1-2.1-1.6-3.9-1.6c-1.5,0-2.8,0.5-3.8,1.3c-1,0.8-1.6,2.1-1.6,3.6H855.5z"/>
            <path className="logo-st0" d="M870.1,593.4h-6.2v-11.3h3.3c2.5-4.3,3.6-10.1,3.4-18h19v18h3.4v11.3h-6.2v-6.1h-16.7V593.4z M876.6,569.2c-0.2,5.2-1,9.7-2.6,12.9h8.8v-12.9H876.6z"/>
          </svg>
        </div>
        
        {props.user ? (
          <div className="user-greeting">
            <p>{t('mainPage.welcome')} {props.user.first_name}!</p>
          </div>
        ) : (
          <div className="user-greeting">
            <p>{t('mainPage.welcome')}</p>
          </div>
        )}
      </header>

      {/* Основной контент */}
      <main className="main-content">
        <div className="welcome-section">
          <h2>{t('mainPage.title')}</h2>
          <p className="subtitle">{t('mainPage.subtitle')}</p>
        </div>

        {/* Поисковая строка с голосовым вводом */}
        <div className="search-container" ref={searchRef}>
          <div className="search-input-wrapper">
            <div className="search-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder={isListening ? "Слушаю..." : "Поиск услуг, врачей, анализов..."}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              onClick={() => searchQuery && performSearch(searchQuery)}
              disabled={isListening}
            />
            
            {/* Кнопка голосового поиска */}
            {voiceSupported && (
              <button 
                className={`voice-search-btn ${isListening ? 'listening' : ''}`}
                onClick={isListening ? stopVoiceSearch : startVoiceSearch}
                type="button"
                title={isListening ? "Остановить запись" : "Голосовой поиск"}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="22"/>
                </svg>
                {isListening && <span className="voice-pulse"></span>}
              </button>
            )}
          </div>
          
          {/* Результаты поиска */}
          {showResults && searchResults.length > 0 && (
            <div className="search-results">
              <div className="search-results-header">
                <span className="results-count">
                  Найдено {searchResults.length} {searchResults.length === 1 ? 'результат' : 
                    searchResults.length > 1 && searchResults.length < 5 ? 'результата' : 'результатов'}
                </span>
              </div>
              <div className="search-results-list">
                {searchResults.map((result, index) => (
                  <div
                    key={result.id}
                    className={`search-result-item ${selectedResultIndex === index ? 'selected' : ''}`}
                    onClick={() => handleResultClick(result)}
                    onMouseEnter={() => setSelectedResultIndex(index)}
                  >
                    <div className="result-content">
                      <div className="result-icon">{result.icon || '🔍'}</div>
                      <div className="result-details">
                        <div className="result-title">{result.title}</div>
                        <div className="result-meta">
                          <span className="result-category">{result.category}</span>
                          {result.parent && (
                            <>
                              <span className="result-separator">•</span>
                              <span className="result-parent">{result.parent}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="result-arrow">→</div>
                  </div>
                ))}
              </div>
              <div className="search-results-footer">
                <span className="search-hint">↵ для перехода</span>
              </div>
            </div>
          )}

          {showResults && searchResults.length === 0 && searchQuery.length >= 1 && (
            <div className="search-results">
              <div className="no-results">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <p>Ничего не найдено</p>
                <p className="no-results-hint">
                  Попробуйте: "кровь", "рентген", "мрт", "врач", "анализы", "педиатр"
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Сетка кнопок */}
        <div className="buttons-grid">
          <button className="service-btn" onClick={props.onOpenPrice}>
            <span className="btn-icon">
              <img 
                src={`${process.env.PUBLIC_URL}/icons/price.png`}
                alt={t('mainPage.price')}
              />
            </span>
            <span className="btn-text">{t('mainPage.price')}</span>
          </button>

          <button 
            className="service-btn" 
            onClick={() => props.onOpenHealthCheck && props.onOpenHealthCheck()}
          >
            <span className="btn-icon">
              <img 
                src={`${process.env.PUBLIC_URL}/icons/checkup.png`}
                alt={t('mainPage.checkup')}
              />
            </span>
            <span className="btn-text">{t('mainPage.checkup')}</span>
          </button>

          {/* Кнопка Прием специалиста */}
          <button 
            className="service-btn" 
            onClick={() => props.onOpenDoctors && props.onOpenDoctors()}
          >
            <span className="btn-icon">
              👨‍⚕️
            </span>
            <span className="btn-text">Прием специалиста</span>
          </button>

          <button className="service-btn" onClick={props.onOpenReview}>
            <span className="btn-icon">
              <img 
                src={`${process.env.PUBLIC_URL}/icons/review.png`}
                alt={t('mainPage.review')}
              />
            </span>
            <span className="btn-text">{t('mainPage.review')}</span>
          </button>

          <button className="service-btn" onClick={props.onOpenFaq}>
            <span className="btn-icon">
              <img 
                src={`${process.env.PUBLIC_URL}/icons/faq.png`}
                alt={t('mainPage.faq')}
              />
            </span>
            <span className="btn-text">{t('mainPage.faq')}</span>
          </button>

          {/* Кнопка Контакты */}
          <button 
            className="service-btn" 
            onClick={handleOpenContacts}
          >
            <span className="btn-icon">
              <img 
                src={`${process.env.PUBLIC_URL}/icons/contacts.png`}
                alt={t('mainPage.contacts')}
              />
            </span>
            <span className="btn-text">{t('mainPage.contacts')}</span>
          </button>

          <button className="service-btn" onClick={handleOpenSupport}>
            <span className="btn-icon">
              <img 
                src={`${process.env.PUBLIC_URL}/icons/support.png`}
                alt={t('mainPage.support')}
              />
            </span>
            <span className="btn-text">{t('mainPage.support')}</span>
          </button>

          {/* Кнопка смены языка */}
          <button 
            className="service-btn language-btn"
            onClick={() => setShowLanguageModal(true)}
          >
            <span className="btn-icon">
              <img 
                src={`${process.env.PUBLIC_URL}/flag/${i18n.language}.png`}
                alt={`${i18n.language} flag`}
                style={{ 
                  width: '28px', 
                  height: '20px', 
                  objectFit: 'contain', 
                  borderRadius: '4px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.1)'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const parent = e.target.parentElement;
                  if (parent) {
                    parent.innerHTML = getCurrentLanguage().flag;
                  }
                }}
              />
            </span>
            <span className="btn-text">
              {t('mainPage.lang')}
            </span>
          </button>
        </div>

        {/* Образовательный блок */}
        {/* <EducationalBlock 
          onOpenFullContent={props.onOpenEducational}
          isFullScreenMode={false}
        /> */}
      </main>

      {/* Футер */}
      <footer className="footer">
        <p>AlabugaMed - Ваше здоровье в надёжных руках</p>
        <p className="footer-info">Круглосуточная поддержка • Лицензированная клиника</p>
      </footer>

      {/* Модальное окно выбора языка */}
      {showLanguageModal && (
        <div className="language-modal-overlay" onClick={() => setShowLanguageModal(false)}>
          <div className="language-modal" onClick={(e) => e.stopPropagation()}>
            <div className="language-modal-header">
              <h3>Выберите язык</h3>
            </div>
            
            <div className="language-modal-body">
              {languages.map((language) => (
                <button
                  key={language.code}
                  className={`language-modal-option ${i18n.language === language.code ? 'active' : ''}`}
                  onClick={() => changeLanguage(language.code)}
                >
                  <div className="language-option-content">
                    <span className="language-flag-big">
                      <img 
                        src={`${process.env.PUBLIC_URL}/flag/${language.code}.png`}
                        alt={`${language.name} flag`}
                        className="flag-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          const parent = e.target.parentElement;
                          if (parent) {
                            parent.innerHTML = language.flag;
                          }
                        }}
                      />
                    </span>
                    <div className="language-info">
                      <span className="language-name-modal">{language.name}</span>
                      {i18n.language === language.code && (
                        <span className="language-current-badge">Текущий</span>
                      )}
                    </div>
                  </div>
                  {i18n.language === language.code && (
                    <span className="language-selected-icon">✓</span>
                  )}
                </button>
              ))}
            </div>
            
            <div className="language-modal-footer">
              <button 
                className="modal-cancel-btn"
                onClick={() => setShowLanguageModal(false)}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}; 

export default MainPage;